import { XIcon } from "@primer/octicons-react";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";
import { TabLabel } from "./tabs";

/** Filter chips: pick one, or click the selected chip again to clear it (value becomes ""). */
function ChipGroup({ className, value, onValueChange, ...props }: Omit<React.ComponentProps<typeof ToggleGroupPrimitive.Root>, "type" | "value" | "onValueChange" | "defaultValue"> & { value: string; onValueChange: (v: string) => void }) {
  return <ToggleGroupPrimitive.Root type="single" value={value} onValueChange={onValueChange} className={cn("flex flex-wrap gap-1.5", className)} {...props} />;
}

const chip =
  "inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-secondary px-2.5 text-xs leading-none text-foreground hover:bg-secondary-hover data-[state=on]:border-muted-foreground data-[state=on]:bg-accent data-[state=on]:font-semibold";

function Chip({ className, ...props }: React.ComponentProps<typeof ToggleGroupPrimitive.Item>) {
  return <ToggleGroupPrimitive.Item className={cn(chip, className)} {...props} />;
}

/** An applied filter outside a ChipGroup, such as one set by clicking a row; clicking clears it. */
function DismissChip({ className, children, onDismiss, label }: { className?: string; children: React.ReactNode; onDismiss: () => void; label: string }) {
  return (
    <button type="button" data-state="on" className={cn(chip, className)} onClick={onDismiss} aria-label={label}>
      {children} <XIcon size={12} className="text-muted-foreground" />
    </button>
  );
}

/**
 * GitHub's Open/Closed toggle above an issue list: icon + count tabs where the active one is bold.
 * Clicking the active tab keeps it; `value` matching no item leaves every tab unselected.
 */
function FilterTabs({ value, onValueChange, items, className }: { value: string; onValueChange: (v: string) => void; items: Array<{ value: string; icon?: React.ReactNode; label: string }>; className?: string }) {
  return (
    <ToggleGroupPrimitive.Root type="single" value={value} onValueChange={(v) => v && onValueChange(v)} className={cn("flex items-center gap-1", className)}>
      {items.map((it) => (
        <ToggleGroupPrimitive.Item
          key={it.value}
          value={it.value}
          className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2 text-sm text-muted-foreground hover:bg-transparent-hover hover:text-foreground data-[state=on]:font-semibold data-[state=on]:text-foreground [&_svg]:shrink-0"
        >
          {it.icon}
          <TabLabel>{it.label}</TabLabel>
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}

/** Primer's SegmentedControl: a compact, always-selected switch between views of the same content. */
function SegmentedControl({ value, onValueChange, items, label, className }: { value: string; onValueChange: (v: string) => void; items: Array<{ value: string; label: string }>; label: string; className?: string }) {
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      aria-label={label}
      value={value}
      onValueChange={(v) => v && onValueChange(v)}
      className={cn("inline-flex h-6 items-center rounded-md border border-border bg-neutral-muted p-px font-sans", className)}
    >
      {items.map((it) => (
        <ToggleGroupPrimitive.Item
          key={it.value}
          value={it.value}
          className="h-full rounded-[5px] px-2 text-xs text-muted-foreground hover:text-foreground data-[state=on]:bg-background data-[state=on]:font-semibold data-[state=on]:text-foreground data-[state=on]:shadow-resting"
        >
          {it.label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}

export { Chip, ChipGroup, DismissChip, FilterTabs, SegmentedControl };
